import { t } from "@lingui/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, TextInput } from "react-native";

import { Button, Input } from "@whoosh/design-system/src/components/controls";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import {
  AlertIcon,
  EyeClosedIcon,
  EyeIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { useSignIn } from "../../../../../../../hooks/useSignIn";
import { proceedToJoinNewClubStepAtom } from "../../../../atoms/actions";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { useSendPasswordResetRequest } from "../../../../hooks/useSendPasswordResetRequest";

const currentVerifiedEmailAtom = atom((get) => {
  const curStateData = get(VerificationModalStepStateAtom);
  if (curStateData.step === "EnterPassword") {
    return curStateData.verifiedEmail;
  }
  return null;
});

export const EnterPasswordStep = ({
  onComplete,
  alternativeSignIn,
}: {
  onComplete: (clubSlug?: string) => void;
  alternativeSignIn?: ReactNode;
}) => {
  const [inputIsPassword, setInputIsPassword] = useState(true);
  const [curPassword, setCurPassword] = useState("");
  const inputRef = useRef<TextInput>(null);
  const curVerifiedEmail = useAtomValue(currentVerifiedEmailAtom);
  const proceedToJoinClub = useSetAtom(proceedToJoinNewClubStepAtom);
  const [sendPasswordReset] = useSendPasswordResetRequest();
  const canSubmit = !!curVerifiedEmail && curPassword.length >= 4;

  const [trySignIn, isLoggingIn, hasError] = useSignIn();

  const handleLoginPress = () => {
    if (!!curVerifiedEmail) {
      trySignIn({ email: curVerifiedEmail, password: curPassword }, (res) => {
        const clientType =
          res.responsePayload?.session?.currentClientProfile?.member
            ?.__typename;
        const needsMemberProfile = clientType === "AnonymousCustomer";
        if (needsMemberProfile) {
          proceedToJoinClub();
        } else {
          const firstProfile =
            res.responsePayload?.session?.user?.memberProfiles?.edges?.[0]
              ?.node;
          const defaultClubSlug = firstProfile?.member?.club?.slug;

          onComplete(defaultClubSlug);
        }
      });
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputIsPassword]);

  const handleSendPasswordReset = () => {
    if (curVerifiedEmail) {
      sendPasswordReset({ email: curVerifiedEmail }, () => {});
    }
  };

  return (
    <Box flex={1} paddingHorizontal={6}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack paddingBottom={6} flexGrow={1}>
          <VStack flexGrow={1} mb={6}>
            <Text
              marginBottom={1}
              variant="mainSmallMedium"
              color="text-secondary"
            >
              {curVerifiedEmail}
            </Text>
            <Text variant={"mainTitle2"}>{t`Enter password`}</Text>
          </VStack>
          <Input<"text">
            hasError={hasError}
            inputRef={inputRef}
            testID="enter-password-step-password-input"
            placeholder={t`Your Password`}
            name="password"
            type={inputIsPassword ? "password" : "text"}
            value={curPassword}
            onChange={setCurPassword}
            rightIcon={
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={
                  inputIsPassword ? t`Show password` : t`Hide password`
                }
                onPress={() => {
                  setInputIsPassword((val) => !val);
                }}
              >
                {inputIsPassword ? <EyeIcon /> : <EyeClosedIcon />}
              </Pressable>
            }
          />
          {hasError ? (
            <Box mt={2}>
              <InfoBanner
                leadingIcon={AlertIcon}
                colorTheme="ERROR"
                message={t`Wrong password. Try again.`}
              />
            </Box>
          ) : null}
        </VStack>
      </ScrollView>
      <VStack gap={2}>
        <Button
          accessibilityLabel={t`Reset Password`}
          variant={"ghost"}
          onPress={handleSendPasswordReset}
          testID="enter-password-step-reset-password-button"
        >
          {t`Reset Password`}
        </Button>
        <Button
          accessibilityLabel={t`Sign In`}
          isDisabled={!canSubmit}
          onPress={handleLoginPress}
          isLoading={isLoggingIn}
          testID="enter-password-step-sign-in-button"
        >{t`Sign In`}</Button>
        {alternativeSignIn}
      </VStack>
    </Box>
  );
};
